import React, { useState, useMemo, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { toast } from 'react-toastify';
import './PnlTracker.css';
import journalApi, { StrategyItem, JournalTrade, AddTradeReq, StockSymbol, TradeStats } from '../../services/journalApi.ts';
import PremiumAccessPage from '../PremiumAccessPage.tsx';
import { usePremium } from '../../hooks/usePremium.ts';
import BulkUploadModal from './BulkUploadModal.tsx';

// ── Constants ─────────────────────────────────────────────────────────────────

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];
const WEEKDAYS = ['Su','Mo','Tu','We','Th','Fr','Sa'];


// ── Types ─────────────────────────────────────────────────────────────────────

type Tab        = 'year' | 'month';
type EntryMode  = 'TRADE' | 'CAPITAL' | 'DIRECT';
type Instrument = 'EQUITY' | 'OPTIONS' | 'FUTURES' | 'OTHER';
type TradeType  = 'INTRADAY' | 'SWING' | 'POSITIONAL';
type Direction  = 'LONG' | 'SHORT';

type Trade = JournalTrade;


// ── Helpers ───────────────────────────────────────────────────────────────────

function pad(n: number) { return String(n).padStart(2, '0'); }
function toDateStr(y: number, m: number, d: number) { return `${y}-${pad(m + 1)}-${pad(d)}`; }
function isFuture(ds: string) {
  const [y, m, d] = ds.split('-').map(Number);
  const date = new Date(y, m - 1, d); // local midnight — avoids UTC parse issue
  const now  = new Date(); now.setHours(0, 0, 0, 0);
  return date > now;
}
function fmtPnl(v: number) { return (v >= 0 ? '+₹' : '-₹') + Math.abs(v).toLocaleString('en-IN'); }

function fmtPnlCompact(v: number) {
  const sign = v >= 0 ? '+₹' : '-₹';
  const abs  = Math.abs(v);
  if (abs >= 10_000_000) {
    const cr = abs / 10_000_000;
    return sign + (cr >= 100 ? Math.round(cr) : +cr.toFixed(1)) + 'Cr';
  }
  if (abs >= 100_000) return sign + +(abs / 100_000).toFixed(1) + 'L';
  if (abs >= 1_000)   return sign + +(abs / 1_000).toFixed(1) + 'K';
  return sign + abs.toLocaleString('en-IN');
}

// For calendar cells: show full amount up to ₹9,00,000 then abbreviate
function fmtPnlCalendar(v: number) {
  const sign = v >= 0 ? '+₹' : '-₹';
  const abs  = Math.abs(v);
  if (abs >= 10_000_000) {
    const cr = abs / 10_000_000;
    return sign + (cr >= 100 ? Math.round(cr) : +cr.toFixed(1)) + 'Cr';
  }
  if (abs >= 900_000) return sign + +(abs / 100_000).toFixed(1) + 'L';
  return sign + abs.toLocaleString('en-IN');
}
function fmtAmt(v: number) { return '₹' + v.toLocaleString('en-IN'); }

// ── Mini Month Card (Year View) ───────────────────────────────────────────────

interface MiniMonthProps {
  year: number; monthIndex: number;
  summary: Record<string, number>;
  onDayClick: (ds: string) => void;
}

const MiniMonth: React.FC<MiniMonthProps> = ({ year, monthIndex, summary, onDayClick }) => {
  const firstDay    = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const today       = new Date();
  const todayStr    = toDateStr(today.getFullYear(), today.getMonth(), today.getDate());
  const prefix      = `${year}-${pad(monthIndex + 1)}`;

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthTotal = Object.entries(summary)
    .filter(([ds]) => ds.startsWith(prefix))
    .reduce((s, [, v]) => s + v, 0);
  const hasData = Object.keys(summary).some(ds => ds.startsWith(prefix));

  return (
    <div className="pnl-mini-month">
      <div className="pnl-mini-header">
        <span className="pnl-mini-name">{MONTHS[monthIndex]}</span>
        {hasData && (
          <span className={`pnl-mini-total ${monthTotal >= 0 ? 'profit' : 'loss'}`} title={fmtPnl(monthTotal)}>
            {fmtPnlCalendar(monthTotal)}
          </span>
        )}
      </div>
      <div className="pnl-mini-weekdays">
        {WEEKDAYS.map(w => <span key={w}>{w}</span>)}
      </div>
      <div className="pnl-mini-days">
        {cells.map((d, i) => {
          if (!d) return <span key={i} className="pnl-mini-day empty" />;
          const ds  = toDateStr(year, monthIndex, d);
          const pnl = summary[ds];
          const cls = pnl !== undefined ? (pnl >= 0 ? 'profit' : 'loss') : '';
          return (
            <span
              key={i}
              className={`pnl-mini-day ${cls} ${ds === todayStr ? 'today' : ''}`}
              onClick={() => onDayClick(ds)}
              title={pnl !== undefined ? fmtPnl(pnl) : `${d} ${MONTHS[monthIndex]}`}
            >
              {d}
            </span>
          );
        })}
      </div>
    </div>
  );
};

// ── Full Month Calendar (Month View) ─────────────────────────────────────────

interface FullMonthProps {
  year: number; month: number;
  summary: Record<string, number>;
  onDayClick: (ds: string) => void;
  onPrev: () => void; onNext: () => void;
  disableNext?: boolean;
}

const FullMonthCalendar: React.FC<FullMonthProps> = ({ year, month, summary, onDayClick, onPrev, onNext, disableNext }) => {
  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today       = new Date();
  const todayStr    = toDateStr(today.getFullYear(), today.getMonth(), today.getDate());

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const prefix     = `${year}-${pad(month + 1)}`;
  const monthTotal = Object.entries(summary).filter(([ds]) => ds.startsWith(prefix)).reduce((s, [, v]) => s + v, 0);
  const hasData    = Object.keys(summary).some(ds => ds.startsWith(prefix));

  return (
    <div className="pnl-full-month">
      <div className="pnl-full-nav">
        <button className="pnl-nav-btn" onClick={onPrev}>‹</button>
        <div className="pnl-full-nav-center">
          <span className="pnl-full-month-label">{MONTHS[month]} {year}</span>
          {hasData && (
            <span className={`pnl-full-month-total ${monthTotal >= 0 ? 'profit' : 'loss'}`} title={fmtPnl(monthTotal)}>
              {fmtPnlCalendar(monthTotal)}
            </span>
          )}
        </div>
        <button className="pnl-nav-btn" onClick={onNext} disabled={disableNext}>›</button>
      </div>

      <div className="pnl-full-weekdays">
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(w => (
          <div key={w} className="pnl-full-wd">{w}</div>
        ))}
      </div>

      <div className="pnl-full-grid">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="pnl-full-day empty" />;
          const ds  = toDateStr(year, month, d);
          const pnl = summary[ds];
          const cls = pnl !== undefined ? (pnl >= 0 ? 'profit' : 'loss') : '';
          const isToday = ds === todayStr;
          return (
            <div
              key={i}
              className={`pnl-full-day ${cls} ${isToday ? 'today' : ''}`}
              onClick={() => onDayClick(ds)}
            >
              <span className={`pnl-full-day-num ${isToday ? 'today' : ''}`}>{d}</span>
              {pnl !== undefined && (
                <span className={`pnl-full-day-pnl ${pnl >= 0 ? 'profit' : 'loss'}`} title={fmtPnl(pnl)}>
                  {fmtPnlCalendar(pnl)}
                </span>
              )}
              {pnl !== undefined && (
                <span className={`pnl-full-day-dot ${pnl >= 0 ? 'profit' : 'loss'}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Symbol Search ─────────────────────────────────────────────────────────────

interface SymbolSearchProps {
  value:    string;
  stocks:   StockSymbol[];
  onChange: (symbol: string) => void;
}

interface SymbolSearchHandle { focus: () => void; }

const SymbolSearch = forwardRef<SymbolSearchHandle, SymbolSearchProps>(({ value, stocks, onChange }, ref) => {
  const [query, setQuery]       = useState(value);
  const [open, setOpen]         = useState(false);
  const [focused, setFocused]   = useState(-1);
  const wrapRef                 = useRef<HTMLDivElement>(null);
  const listRef                 = useRef<HTMLUListElement>(null);
  const inputRef                = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({ focus: () => inputRef.current?.focus() }));

  const filtered = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    const bySymbol = stocks.filter(s => s.symbol.toLowerCase().startsWith(q));
    const byName   = stocks.filter(s =>
      !s.symbol.toLowerCase().startsWith(q) && s.name.toLowerCase().includes(q)
    );
    return [...bySymbol, ...byName].slice(0, 50);
  }, [query, stocks]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (s: StockSymbol) => {
    onChange(s.symbol);
    setQuery(s.symbol);
    setOpen(false);
    setFocused(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || !filtered.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setFocused(f => Math.min(f + 1, filtered.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setFocused(f => Math.max(f - 1, 0)); }
    if (e.key === 'Enter' && focused >= 0) { e.preventDefault(); select(filtered[focused]); }
    if (e.key === 'Escape') setOpen(false);
  };

  useEffect(() => {
    if (focused >= 0 && listRef.current) {
      listRef.current.children[focused]?.scrollIntoView({ block: 'nearest' });
    }
  }, [focused]);

  return (
    <div className="sym-search-wrap" ref={wrapRef}>
      <input
        ref={inputRef}
        value={query}
        placeholder="e.g. RELIANCE"
        autoFocus
        autoComplete="off"
        onChange={e => { setQuery(e.target.value); onChange(e.target.value.toUpperCase()); setOpen(true); setFocused(-1); }}
        onFocus={() => { if (query) setOpen(true); }}
        onKeyDown={handleKeyDown}
      />
      {open && filtered.length > 0 && (
        <ul className="sym-dropdown" ref={listRef}>
          {filtered.map((s, i) => (
            <li
              key={s.symbol}
              className={i === focused ? 'active' : ''}
              onMouseDown={() => select(s)}
            >
              <span className="sym-dd-symbol">{s.symbol}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

// ── Add Trade Form ────────────────────────────────────────────────────────────

interface AddTradeFormProps {
  dateStr:     string;
  clearDates?: boolean;
  strategies:  StrategyItem[];
  stocks:      StockSymbol[];
  onSave:      (req: AddTradeReq) => void;
  onCancel:    () => void;
}

const AddTradeForm: React.FC<AddTradeFormProps> = ({ dateStr, clearDates, strategies, stocks, onSave, onCancel }) => {
  const [mode, setMode]           = useState<EntryMode>('TRADE');
  const [symbol, setSymbol]       = useState('');
  const [exchange, setExchange]   = useState('NSE');
  const [instrument, setInstrument] = useState<Instrument>('EQUITY');
  const [strategy, setStrategy]   = useState('');
  const [tradeType, setTradeType] = useState<TradeType>('SWING');
  const [direction, setDirection] = useState<Direction>('LONG');
  const [entryDate, setEntryDate] = useState('');
  const [exitDate, setExitDate]   = useState(clearDates ? '' : dateStr);
  // TRADE mode
  const [qty, setQty]             = useState('');
  const [entryP, setEntryP]       = useState('');
  const [exitP, setExitP]         = useState('');
  // CAPITAL mode
  const [capUsed, setCapUsed]     = useState('');
  const [endCap, setEndCap]       = useState('');
  // DIRECT mode
  const [directPnl, setDirectPnl] = useState('');
  // Optional
  const [notes, setNotes]         = useState('');
  const [error, setError]         = useState('');

  const symbolRef    = useRef<SymbolSearchHandle>(null);
  const entryDateRef = useRef<HTMLInputElement>(null);
  const exitDateRef  = useRef<HTMLInputElement>(null);
  const strategyRef  = useRef<HTMLSelectElement>(null);
  const qtyRef       = useRef<HTMLInputElement>(null);
  const entryPRef    = useRef<HTMLInputElement>(null);
  const exitPRef     = useRef<HTMLInputElement>(null);
  const capUsedRef   = useRef<HTMLInputElement>(null);
  const endCapRef    = useRef<HTMLInputElement>(null);
  const directPnlRef = useRef<HTMLInputElement>(null);

  const isShort = direction === 'SHORT';

  const preview = useMemo(() => {
    if (mode === 'TRADE') {
      const q = parseFloat(qty), ep = parseFloat(entryP), xp = parseFloat(exitP);
      if (!isNaN(q) && !isNaN(ep) && !isNaN(xp) && q > 0 && ep > 0 && xp > 0) {
        const cap = q * ep, end = q * xp;
        // LONG: profit when exit > entry; SHORT: profit when entry > exit (covered lower)
        const pnl = isShort ? cap - end : end - cap;
        return { cap, end, pnl, pct: (pnl / cap) * 100 };
      }
    }
    if (mode === 'CAPITAL') {
      const cap = parseFloat(capUsed), end = parseFloat(endCap);
      if (!isNaN(cap) && !isNaN(end) && cap > 0) {
        // LONG: end - cap; SHORT: cap - end (sold high, covered low)
        const pnl = isShort ? cap - end : end - cap;
        return { cap, end, pnl, pct: (pnl / cap) * 100 };
      }
    }
    if (mode === 'DIRECT') {
      const pnl = parseFloat(directPnl);
      if (!isNaN(pnl)) return { cap: null, end: null, pnl, pct: null };
    }
    return null;
  }, [mode, direction, isShort, qty, entryP, exitP, capUsed, endCap, directPnl]);

  const handleSave = () => {
    setError('');
    if (!symbol.trim())      { setError('Symbol is required.');                    symbolRef.current?.focus();    return; }
    if (!strategy)           { setError('Please select a strategy.');                  strategyRef.current?.focus();  return; }
    if (!entryDate)          { setError('Entry date is required.');                    entryDateRef.current?.focus(); return; }
    if (isFuture(entryDate)) { setError('Entry date cannot be in the future.');        entryDateRef.current?.focus(); return; }
    if (!exitDate)           { setError('Exit date is required.');                     exitDateRef.current?.focus();  return; }
    if (isFuture(exitDate))  { setError('Exit date cannot be in the future.');         exitDateRef.current?.focus();  return; }
    if (exitDate < entryDate){ setError('Exit date cannot be before entry date.');     exitDateRef.current?.focus();  return; }
    if (mode === 'TRADE' && !qty)    { setError('Fill Quantity, Entry Price and Exit Price.'); qtyRef.current?.focus();    return; }
    if (mode === 'TRADE' && !entryP) { setError('Fill Quantity, Entry Price and Exit Price.'); entryPRef.current?.focus(); return; }
    if (mode === 'TRADE' && !exitP)  { setError('Fill Quantity, Entry Price and Exit Price.'); exitPRef.current?.focus();  return; }
    if (mode === 'CAPITAL' && !capUsed) { setError('Fill Buy Capital and End Capital.'); capUsedRef.current?.focus(); return; }
    if (mode === 'CAPITAL' && !endCap)  { setError('Fill Buy Capital and End Capital.'); endCapRef.current?.focus();  return; }
    if (mode === 'DIRECT' && !directPnl){ setError('Fill P&L Amount.');               directPnlRef.current?.focus(); return; }
    if (!preview)            { setError('Check the numbers entered.');                                                return; }

    onSave({
      symbol:         symbol.toUpperCase().trim(),
      exchange,
      instrumentType: instrument,
      strategyId:     parseInt(strategy),
      entryMode:      mode,
      tradeType,
      direction,
      entryDate,
      exitDate,
      quantity:       qty    ? parseInt(qty)        : null,
      entryPrice:     entryP ? parseFloat(entryP)   : null,
      exitPrice:      exitP  ? parseFloat(exitP)    : null,
      capitalUsed:    preview.cap ?? (capUsed ? parseFloat(capUsed) : null),
      endCapital:     preview.end ?? (endCap  ? parseFloat(endCap)  : null),
      pnlAmount:      preview.pnl,
      pnlPercent:     preview.pct ?? null,
      notes:          notes.trim() || null,
    });
  };

  return (
    <div className="pnl-add-form">

      {/* Row 1: Symbol, Exchange, Instrument */}
      <div className="pnl-form-row cols-sym-row">
        <label>Symbol
          <SymbolSearch ref={symbolRef} value={symbol} stocks={stocks} onChange={val => setSymbol(val)} />
        </label>
        <label>Exchange
          <select value={exchange} onChange={e => setExchange(e.target.value)}>
            <option>NSE</option><option>BSE</option>
          </select>
        </label>
        <label>Instrument
          <select value={instrument} onChange={e => setInstrument(e.target.value as Instrument)}>
            <option value="EQUITY">Equity</option>
            <option value="OPTIONS">Options</option>
            <option value="FUTURES">Futures</option>
            <option value="OTHER">Other</option>
          </select>
        </label>
      </div>

      {/* Row 2: Strategy, Trade Type, Direction */}
      <div className="pnl-form-row cols-3">
        <label>Strategy
          <select ref={strategyRef} value={strategy} onChange={e => setStrategy(e.target.value)}>
            <option value="" disabled>Select strategy…</option>
            {strategies.map(s => <option key={s.id} value={String(s.id)}>{s.name}{s.isCustom ? ' ★' : ''}</option>)}
          </select>
        </label>
        <label>Trade Type
          <select value={tradeType} onChange={e => setTradeType(e.target.value as TradeType)}>
            <option value="SWING">Swing</option>
            <option value="INTRADAY">Intraday</option>
            <option value="POSITIONAL">Positional</option>
          </select>
        </label>
        <label>Direction
          <div className="pnl-dir-toggle">
            <button type="button" className={direction === 'LONG'  ? 'active long'  : ''} onClick={() => setDirection('LONG')}>▲ Long</button>
            <button type="button" className={direction === 'SHORT' ? 'active short' : ''} onClick={() => setDirection('SHORT')}>▼ Short</button>
          </div>
        </label>
      </div>

      {/* Row 3: Dates */}
      <div className="pnl-form-row cols-2">
        <label>Entry Date
          <input ref={entryDateRef} type="date" value={entryDate} onChange={e => setEntryDate(e.target.value)} />
        </label>
        <label>Exit Date
          <input ref={exitDateRef} type="date" value={exitDate} onChange={e => setExitDate(e.target.value)} />
        </label>
      </div>

      <div className="pnl-form-sep" />

      {/* Entry Mode Tabs */}
      <div className="pnl-mode-label">How do you want to log this trade? <span>Choose one of three options</span></div>
      <div className="pnl-mode-tabs">
        <button type="button" className={mode === 'TRADE'   ? 'active' : ''} onClick={() => setMode('TRADE')}>Trade Details</button>
        <button type="button" className={mode === 'CAPITAL' ? 'active' : ''} onClick={() => setMode('CAPITAL')}>Capital Amount</button>
        <button type="button" className={mode === 'DIRECT'  ? 'active' : ''} onClick={() => setMode('DIRECT')}>Direct P&amp;L</button>
      </div>

      {mode === 'TRADE' && (
        <div className="pnl-form-row cols-3">
          <label>Quantity
            <input ref={qtyRef}    type="number" value={qty}    onChange={e => setQty(e.target.value)}    placeholder="0" min="0" />
          </label>
          <label>Entry Price (₹)
            <input ref={entryPRef} type="number" value={entryP} onChange={e => setEntryP(e.target.value)} placeholder="0.00" min="0" />
          </label>
          <label>Exit Price (₹)
            <input ref={exitPRef}  type="number" value={exitP}  onChange={e => setExitP(e.target.value)}  placeholder="0.00" min="0" />
          </label>
        </div>
      )}

      {mode === 'CAPITAL' && (
        <div className="pnl-form-row cols-2">
          <label>{isShort ? 'Sell Capital (₹)' : 'Buy Capital (₹)'}
            <input ref={capUsedRef} type="number" value={capUsed} onChange={e => setCapUsed(e.target.value)} placeholder="0.00" min="0" />
          </label>
          <label>{isShort ? 'Cover Capital (₹)' : 'End Capital (₹)'}
            <input ref={endCapRef}  type="number" value={endCap}  onChange={e => setEndCap(e.target.value)}  placeholder="0.00" min="0" />
          </label>
        </div>
      )}

      {mode === 'DIRECT' && (
        <div className="pnl-form-row cols-1">
          <label>P&amp;L Amount (₹) — enter negative for loss
            <input ref={directPnlRef} type="number" value={directPnl} onChange={e => setDirectPnl(e.target.value)} placeholder="e.g. 2500 or -800" />
          </label>
        </div>
      )}

      {preview && (
        <div className={`pnl-preview-bar ${preview.pnl >= 0 ? 'profit' : 'loss'}`}>
          {preview.cap != null && <span>{isShort ? 'Sell Capital' : 'Buy Capital'}: <strong>{fmtAmt(preview.cap)}</strong></span>}
          {preview.end != null && <span>{isShort ? 'Cover Capital' : 'End Capital'}: <strong>{fmtAmt(preview.end)}</strong></span>}
          <span>P&amp;L: <strong>{fmtPnl(preview.pnl)}</strong></span>
          {preview.pct != null && <span className={preview.pct >= 0 ? 'profit' : 'loss'}>({preview.pct >= 0 ? '+' : ''}{preview.pct.toFixed(2)}%)</span>}
        </div>
      )}

      <div className="pnl-form-sep" />

      {/* Optional fields */}
      <div className="pnl-form-row cols-1">
        <label>Notes <span className="pnl-opt">optional</span>
          <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. VCP breakout on volume" />
        </label>
      </div>

      {error && <div className="pnl-form-error">{error}</div>}

      <div className="pnl-form-actions">
        <button type="button" className="pnl-btn-cancel" onClick={onCancel}>Cancel</button>
        <button type="button" className="pnl-btn-save"   onClick={handleSave}>Save Trade</button>
      </div>
    </div>
  );
};

// ── Day Modal ─────────────────────────────────────────────────────────────────

interface DayModalProps {
  dateStr:              string;
  trades:               Trade[];
  loading:              boolean;
  strategies:           StrategyItem[];
  stocks:               StockSymbol[];
  onClose:              () => void;
  onAddTrade:           (req: AddTradeReq) => Promise<void>;
  onDeleteTrade:        (id: number) => void;
  openFormImmediately?: boolean;
}

const DayModal: React.FC<DayModalProps> = ({ dateStr, trades, loading, strategies, stocks, onClose, onAddTrade, onDeleteTrade, openFormImmediately }) => {
  const [showForm, setShowForm]         = useState(openFormImmediately ?? false);
  const [saving,   setSaving]           = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const [y, m, d] = dateStr.split('-');
  const label  = `${parseInt(d)} ${MONTHS[parseInt(m) - 1]} ${y}`;
  const netPnl = trades.reduce((s, t) => s + t.pnlAmount, 0);

  const handleSave = async (req: AddTradeReq) => {
    setSaving(true);
    try {
      await onAddTrade(req);
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="pnl-overlay"
      ref={overlayRef}
      onClick={e => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="pnl-modal">

        <div className="pnl-modal-header">
          <div className="pnl-modal-title-row">
            <span className="pnl-modal-date">{label}</span>
            {trades.length > 0 && (
              <span className={`pnl-modal-net ${netPnl >= 0 ? 'profit' : 'loss'}`}>
                {fmtPnl(netPnl)}
              </span>
            )}
          </div>
          <button className="pnl-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="pnl-modal-body">

          {loading && (
            <div className="pnl-day-loading">
              <div className="pnl-spinner" />
              <span>Loading trades…</span>
            </div>
          )}

          {!loading && trades.length === 0 && !showForm && (
            <div className="pnl-no-trades">
              <span className="pnl-no-trades-icon">📋</span>
              <p>No trades recorded for this day</p>
              <span>Click <strong>+ Add Trade</strong> to log your first trade</span>
            </div>
          )}

          {trades.map(t => (
            <div key={t.id} className={`pnl-trade-card ${t.pnlAmount >= 0 ? 'profit' : 'loss'}`}>
              <div className="pnl-trade-top">
                <div className="pnl-trade-left">
                  <span className="pnl-trade-symbol">{t.symbol}</span>
                  <span className="pnl-badge">{t.exchange}</span>
                  <span className="pnl-badge">{t.instrumentType}</span>
                  <span className={`pnl-badge direction ${t.direction.toLowerCase()}`}>
                    {t.direction === 'LONG' ? '▲' : '▼'} {t.direction}
                  </span>
                </div>
                <div className="pnl-trade-right">
                  <span className={`pnl-trade-pnl ${t.pnlAmount >= 0 ? 'profit' : 'loss'}`}>
                    {fmtPnl(t.pnlAmount)}
                    {t.pnlPercent != null && (
                      <span className="pnl-trade-pct">
                        {' '}({t.pnlPercent >= 0 ? '+' : ''}{t.pnlPercent.toFixed(2)}%)
                      </span>
                    )}
                  </span>
                  <button className="pnl-delete-btn" onClick={() => setConfirmDeleteId(t.id)} title="Remove trade">✕</button>
                </div>
              </div>

              <div className="pnl-trade-info">
                {t.quantity != null && t.entryPrice != null && t.exitPrice != null && (
                  <span>Qty {t.quantity} · ₹{t.entryPrice} → ₹{t.exitPrice}</span>
                )}
                {t.capitalUsed != null && t.endCapital != null && (
                  <span>{fmtAmt(t.capitalUsed)} → {fmtAmt(t.endCapital)}</span>
                )}
              </div>

              <div className="pnl-trade-meta-row">
                <span className="pnl-badge-sm">{t.tradeType}</span>
                {t.strategyName && (
                  <span className="pnl-badge-sm strategy">{t.strategyName}</span>
                )}
              </div>

              {t.notes && <div className="pnl-trade-notes">"{t.notes}"</div>}
            </div>
          ))}

          {showForm
            ? <AddTradeForm dateStr={dateStr} clearDates={openFormImmediately} strategies={strategies} stocks={stocks} onSave={handleSave} onCancel={() => setShowForm(false)} />
            : <button className="pnl-add-trade-btn" onClick={() => setShowForm(true)} disabled={loading}>+ Add Trade</button>
          }
          {saving && <div className="pnl-saving-bar">Saving trade…</div>}
        </div>

        {confirmDeleteId !== null && (
          <div className="pnl-confirm-overlay">
            <div className="pnl-confirm-dialog">
              <p className="pnl-confirm-msg">Delete this trade? This cannot be undone.</p>
              <div className="pnl-confirm-actions">
                <button className="pnl-confirm-cancel" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                <button className="pnl-confirm-delete" onClick={() => { onDeleteTrade(confirmDeleteId); setConfirmDeleteId(null); }}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const PnlTracker: React.FC = () => {
  const isPremium = usePremium();

  const now          = new Date();
  const currentYear  = now.getFullYear();
  const currentMonth = now.getMonth();

  const [tab,          setTab]          = useState<Tab>('year');
  const [year,         setYear]         = useState(currentYear);
  const [viewMonth,    setViewMonth]    = useState(currentMonth);
  const [viewYear,     setViewYear]     = useState(currentYear);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [quickAddOpen,   setQuickAddOpen]   = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const todayStr = toDateStr(now.getFullYear(), now.getMonth(), now.getDate());
  const [summary,      setSummary]      = useState<Record<string, number>>({});
  const [allTrades,    setAllTrades]    = useState<Record<string, Trade[]>>({});
  const [strategies,   setStrategies]   = useState<StrategyItem[]>([]);
  const [stocks,       setStocks]       = useState<StockSymbol[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [dayLoading,     setDayLoading]     = useState(false);
  const [tradeStats,     setTradeStats]     = useState<TradeStats | null>(null);

  const stats = useMemo(() => {
    const entries = Object.entries(summary).filter(([ds]) => ds.startsWith(`${year}-`));
    const netPnl  = entries.reduce((s, [, v]) => s + v, 0);
    return { netPnl };
  }, [summary, year]);

  // Load strategies + stocks once on mount — only for premium users
  useEffect(() => {
    if (!isPremium) return;
    journalApi.getStrategies()
      .then(r => setStrategies(r.data.data))
      .catch(() => toast.error('Failed to load strategies'));
    journalApi.getStocks()
      .then(r => setStocks(r.data.data))
      .catch(() => {}); // non-critical — user can still type manually
  }, [isPremium]);

  // Load full-year summary + trade-level stats whenever year changes
  useEffect(() => {
    if (!isPremium) return;
    setSummaryLoading(true);
    setTradeStats(null);
    Promise.all([
      journalApi.getSummary(year),
      journalApi.getStats(year),
    ])
      .then(([summaryRes, statsRes]) => {
        setSummary(summaryRes.data.data);
        setTradeStats(statsRes.data.data);
      })
      .catch(() => toast.error('Failed to load summary'))
      .finally(() => setSummaryLoading(false));
  }, [isPremium, year]);

  // Load day trades lazily when a date is selected (cache in allTrades)
  useEffect(() => {
    if (!isPremium || !selectedDate || allTrades[selectedDate] !== undefined) return;
    setDayLoading(true);
    journalApi.getTradesForDate(selectedDate)
      .then(r => setAllTrades(prev => ({ ...prev, [selectedDate]: r.data.data })))
      .catch(() => toast.error('Failed to load trades'))
      .finally(() => setDayLoading(false));
  }, [isPremium, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Year view: clicking day → switch to month tab + open modal
  const handleYearDayClick = (ds: string) => {
    if (isFuture(ds)) {
      toast.warning("Can't log trades for future dates.", { toastId: 'future-date', autoClose: 2500 });
      return;
    }
    const [y, m] = ds.split('-');
    setViewYear(parseInt(y));
    setViewMonth(parseInt(m) - 1);
    setTab('month');
    setQuickAddOpen(false);
    setSelectedDate(ds);
  };

  const handleAddTrade = useCallback(async (req: AddTradeReq): Promise<void> => {
    const { data } = await journalApi.addTrade(req);
    const trade = data.data;
    const ds = trade.exitDate;
    setAllTrades(prev => ({ ...prev, [ds]: [...(prev[ds] ?? []), trade] }));
    setSummary(prev => ({ ...prev, [ds]: (prev[ds] ?? 0) + trade.pnlAmount }));
    journalApi.getStats(year).then(r => setTradeStats(r.data.data)).catch(() => {});
  }, [year]);

  const refreshSummary = useCallback(() => {
    setSummaryLoading(true);
    setTradeStats(null);
    setAllTrades({});
    Promise.all([journalApi.getSummary(year), journalApi.getStats(year)])
      .then(([s, st]) => { setSummary(s.data.data); setTradeStats(st.data.data); })
      .catch(() => toast.error('Failed to refresh summary'))
      .finally(() => setSummaryLoading(false));
  }, [year]);

  const handleDeleteTrade = useCallback(async (ds: string, id: number) => {
    try {
      await journalApi.deleteTrade(id);
      setAllTrades(prev => {
        const updated = (prev[ds] ?? []).filter(t => t.id !== id);
        setSummary(s => {
          const next = { ...s };
          if (updated.length === 0) delete next[ds];
          else next[ds] = updated.reduce((sum, t) => sum + t.pnlAmount, 0);
          return next;
        });
        return { ...prev, [ds]: updated };
      });
      journalApi.getStats(year).then(r => setTradeStats(r.data.data)).catch(() => {});
    } catch {
      toast.error('Failed to delete trade');
    }
  }, [year]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const isAtCurrentMonth = viewYear === now.getFullYear() && viewMonth === now.getMonth();

  const nextMonth = () => {
    if (isAtCurrentMonth) return;
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  if (!isPremium) return <PremiumAccessPage />;

  return (
    <div className="pnl-page">

      {/* ── Header ── */}
      <div className="pnl-header">
        <div className="pnl-header-top">
          <div className="pnl-header-left">
            <h1 className="pnl-page-title">Trading Journal</h1>
            <p className="pnl-page-sub">Track, review and improve your trades</p>
          </div>
          <div className="pnl-year-nav">
            <button onClick={() => setYear(y => y - 1)}>‹</button>
            <span className="pnl-year-label">{year}</span>
            <button onClick={() => setYear(y => y + 1)} disabled={year >= currentYear}>›</button>
          </div>
        </div>

        <div className="pnl-stats-row">
          <div className="pnl-stat highlight">
            <span className="pnl-stat-label">
              Net P&amp;L
              <span className="info-icon" title="Total profit or loss across all trading days in the selected year.">&#9432;</span>
            </span>
            <span className={`pnl-stat-val ${stats.netPnl >= 0 ? 'profit' : 'loss'}`} title={fmtPnl(stats.netPnl)}>{fmtPnlCompact(stats.netPnl)}</span>
          </div>
          <div className="pnl-stat">
            <span className="pnl-stat-label">
              Win Rate
              <span className="info-icon" title="Percentage of individual trades that were profitable. Formula: Winning Trades ÷ Total Trades × 100.">&#9432;</span>
            </span>
            <span className="pnl-stat-val profit">
              {tradeStats != null ? `${tradeStats.winRate}%` : '—'}
            </span>
          </div>
          <div className="pnl-stat">
            <span className="pnl-stat-label">
              Winners
              <span className="info-icon" title="Number of individual trades that closed in profit.">&#9432;</span>
            </span>
            <span className="pnl-stat-val profit">
              {tradeStats != null ? `▲ ${tradeStats.winningTrades}` : '—'}
            </span>
          </div>
          <div className="pnl-stat">
            <span className="pnl-stat-label">
              Losers
              <span className="info-icon" title="Number of individual trades that closed in loss.">&#9432;</span>
            </span>
            <span className="pnl-stat-val loss">
              {tradeStats != null ? `▼ ${tradeStats.losingTrades}` : '—'}
            </span>
          </div>
          <div className="pnl-stat">
            <span className="pnl-stat-label">
              Best Trade
              <span className="info-icon" title="Highest profit from a single trade this year.">&#9432;</span>
            </span>
            <span className="pnl-stat-val profit" title={tradeStats?.bestTrade != null ? fmtPnl(tradeStats.bestTrade) : undefined}>
              {tradeStats?.bestTrade != null ? fmtPnlCompact(tradeStats.bestTrade) : '—'}
            </span>
          </div>
          <div className="pnl-stat">
            <span className="pnl-stat-label">
              Worst Trade
              <span className="info-icon" title="Largest loss from a single trade this year.">&#9432;</span>
            </span>
            <span className="pnl-stat-val loss" title={tradeStats?.worstTrade != null ? fmtPnl(tradeStats.worstTrade) : undefined}>
              {tradeStats?.worstTrade != null ? fmtPnlCompact(tradeStats.worstTrade) : '—'}
            </span>
          </div>
          <div className="pnl-stat">
            <span className="pnl-stat-label">
              Total Trades
              <span className="info-icon" title="Total number of individual trades logged this year.">&#9432;</span>
            </span>
            <span className="pnl-stat-val">
              {tradeStats != null ? tradeStats.totalTrades : '—'}
            </span>
          </div>
          <div className="pnl-stat">
            <span className="pnl-stat-label">
              Avg Winner
              <span className="info-icon" title="Average profit per winning trade.">&#9432;</span>
            </span>
            <span className="pnl-stat-val profit">
              {tradeStats?.avgWin != null ? fmtPnlCompact(tradeStats.avgWin) : '—'}
            </span>
          </div>
          <div className="pnl-stat">
            <span className="pnl-stat-label">
              Avg Loser
              <span className="info-icon" title="Average loss per losing trade.">&#9432;</span>
            </span>
            <span className="pnl-stat-val loss">
              {tradeStats?.avgLoss != null ? fmtPnlCompact(tradeStats.avgLoss) : '—'}
            </span>
          </div>
          <div className="pnl-stat">
            <span className="pnl-stat-label">
              Profit Factor
              <span className="info-icon" title="Gross profit ÷ gross loss. Above 1 means you make more than you lose overall. 2.0 means you make ₹2 for every ₹1 lost.">&#9432;</span>
            </span>
            <span className={`pnl-stat-val ${tradeStats?.profitFactor != null && tradeStats.profitFactor >= 1 ? 'profit' : 'loss'}`}>
              {tradeStats == null
                ? '—'
                : tradeStats.profitFactor == null
                  ? '∞'
                  : tradeStats.profitFactor.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="pnl-tabs">
        <button className={tab === 'year'  ? 'active' : ''} onClick={() => setTab('year')}>
          <span>📅</span> Year View
        </button>
        <button className={tab === 'month' ? 'active' : ''} onClick={() => setTab('month')}>
          <span>🗓️</span> Month View
        </button>
        <div className="pnl-tabs-hint">
          {tab === 'year' ? 'Click any day to view month details' : 'Click any day to add or view trades'}
        </div>
        <button
          className="pnl-import-btn"
          onClick={() => setShowBulkUpload(true)}
        >
          ⬆ Import Excel
        </button>
        <button
          className="pnl-new-trade-btn"
          onClick={() => { setSelectedDate(todayStr); setQuickAddOpen(true); }}
        >
          + Add Trade
        </button>
      </div>

      {/* ── Year View ── */}
      {tab === 'year' && (
        <div className="pnl-year-grid">
          {Array.from({ length: 12 }, (_, i) => (
            <MiniMonth
              key={i}
              year={year}
              monthIndex={i}
              summary={summary}
              onDayClick={handleYearDayClick}
            />
          ))}
        </div>
      )}

      {/* ── Month View ── */}
      {tab === 'month' && (
        <FullMonthCalendar
          year={viewYear}
          month={viewMonth}
          summary={summary}
          onDayClick={(ds) => {
            if (isFuture(ds)) {
              toast.warning("Can't log trades for future dates.", { toastId: 'future-date', autoClose: 2500 });
              return;
            }
            setQuickAddOpen(false);
            setSelectedDate(ds);
          }}
          onPrev={prevMonth}
          onNext={nextMonth}
          disableNext={isAtCurrentMonth}
        />
      )}

      {/* ── Bulk Upload Modal ── */}
      {showBulkUpload && (
        <BulkUploadModal
          strategies={strategies}
          onClose={() => setShowBulkUpload(false)}
          onDone={refreshSummary}
        />
      )}

      {/* ── Day Modal ── */}
      {selectedDate && (
        <DayModal
          dateStr={selectedDate}
          trades={allTrades[selectedDate] ?? []}
          loading={dayLoading}
          strategies={strategies}
          stocks={stocks}
          onClose={() => { setSelectedDate(null); setQuickAddOpen(false); }}
          onAddTrade={handleAddTrade}
          onDeleteTrade={id => handleDeleteTrade(selectedDate, id)}
          openFormImmediately={quickAddOpen}
        />
      )}
    </div>
  );
};

export default PnlTracker;
